import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import useSubscriptionApi from '../../hooks/useSubscriptionApi';
import useCountdownRedirect from '../../hooks/useCountdownRedirect';
import { SUPPORT_EMAIL } from '../../utils/constants';
import '../../styles/SubscriptionSuccess.css';

/**
 * SubscriptionCancel Component
 *
 * Page shown when user cancels the checkout process.
 * Features:
 * - Shows current subscription status (if any)
 * - Offers trial for new users
 * - Auto-redirects to plans page
 */
const SubscriptionCancel = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {
        subscriptionData,
        canStartTrial,
        refreshSubscriptionStatus,
        getSubscriptionStatusText,
        formatDate
    } = useSubscription();

    const { startTrial, error: apiError, clearError } = useSubscriptionApi();

    const [isStartingTrial, setIsStartingTrial] = useState(false);
    const [error, setError] = useState(null);

    // Countdown redirect to plans page
    const { countdown } = useCountdownRedirect('/subscription/plans', 10);

    // Refresh subscription and log cancelled session
    useEffect(() => {
        const sessionId = new URLSearchParams(location.search).get('session_id');
        if (sessionId) {
            console.log('Checkout cancelled:', sessionId);
        }

        refreshSubscriptionStatus();
    }, [location, refreshSubscriptionStatus]);

    /**
     * Handle starting trial
     */
    const handleStartTrial = async () => {
        try {
            setIsStartingTrial(true);
            setError(null);
            clearError();

            await startTrial();
            await refreshSubscriptionStatus();
            navigate('/dashboard');
        } catch (err) {
            setError(
                apiError ||
                err.message ||
                'Nie udało się rozpocząć okresu próbnego.'
            );
        } finally {
            setIsStartingTrial(false);
        }
    };

    /**
     * Handle contact support
     */
    const handleContactSupport = () => {
        const subject = encodeURIComponent('Pomoc z subskrypcją');
        const body = encodeURIComponent(
            'Witam,\n\nAnulowałem proces płatności i potrzebuję pomocy.\n\nPozdrawiam'
        );
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    };

    const hasActiveSubscription = subscriptionData?.isActive;
    const showTrialOption = canStartTrial();

    return (
        <div className="subscription-result-container">
            <div className="result-card cancel">
                <div className="icon">×</div>

                <h1>Płatność Anulowana</h1>

                <p className="message">
                    Proces płatności został anulowany. Nie pobrano żadnych opłat.
                    {hasActiveSubscription
                        ? ' Twoja obecna subskrypcja pozostaje aktywna.'
                        : ' Możesz spróbować ponownie lub skontaktować się z nami.'}
                </p>

                {/* Current subscription info */}
                <CurrentStatusSection
                    subscriptionData={subscriptionData}
                    getStatusText={getSubscriptionStatusText}
                    formatDate={formatDate}
                />

                {/* Trial offer for new users */}
                {showTrialOption && (
                    <TrialOfferSection
                        isStartingTrial={isStartingTrial}
                        onStartTrial={handleStartTrial}
                    />
                )}

                {/* Error message */}
                {error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {/* Help section */}
                <HelpSection onContactSupport={handleContactSupport} />

                <p className="redirect-info">
                    Przekierowanie do planów za {countdown} sekund...
                </p>

                <div className="actions">
                    <button
                        className="primary-btn"
                        onClick={() => navigate('/subscription/plans')}
                    >
                        Wybierz plan
                    </button>
                    <button
                        className="secondary-btn"
                        onClick={() => navigate('/dashboard')}
                    >
                        Wróć do panelu
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Current subscription status section
 */
const CurrentStatusSection = ({ subscriptionData, getStatusText, formatDate }) => {
    if (!subscriptionData || subscriptionData.status === 'NONE') {
        return null;
    }

    return (
        <div className="current-status">
            <h3>Status subskrypcji</h3>
            <p>
                <strong>Status:</strong> {getStatusText(subscriptionData.status)}
            </p>
            {subscriptionData.endDate && (
                <p>
                    <strong>Data zakończenia:</strong> {formatDate(subscriptionData.endDate)}
                </p>
            )}
            {subscriptionData.daysRemaining > 0 && (
                <p>
                    <strong>Pozostało dni:</strong> {subscriptionData.daysRemaining}
                </p>
            )}
        </div>
    );
};

/**
 * Trial offer section
 */
const TrialOfferSection = ({ isStartingTrial, onStartTrial }) => (
    <div className="trial-offer">
        <h3>🎁 Bezpłatny okres próbny</h3>
        <p>
            Nie jesteś pewien? Wypróbuj za darmo przez 30 dni,
            bez podawania karty płatniczej.
        </p>
        <button
            className="trial-btn"
            onClick={onStartTrial}
            disabled={isStartingTrial}
        >
            {isStartingTrial ? 'Aktywowanie...' : 'Rozpocznij bezpłatnie'}
        </button>
    </div>
);

/**
 * Help section
 */
const HelpSection = ({ onContactSupport }) => (
    <div className="help-section">
        <p>
            Masz pytania lub napotkałeś problem?
        </p>
        <button className="support-btn" onClick={onContactSupport}>
            Skontaktuj się z nami
        </button>
    </div>
);

export default SubscriptionCancel;
